import { Dialog } from "primereact/dialog"
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useState } from "react";
import NewPanelSection from "./newPanelSection";
import OldPanelsSection from "./oldPanelsSection";
import { Button } from "primereact/button";
import { useSaveMyPanel, useUpdateMyPanel } from "../../hooks/useGetDataQuery";
import { useToastContext } from "../../Lib/Contexts/toastContext";
import EditMode from "./editMode";

const SELECTBUTTON_OPTIONS = [
  { name: "newPanel", value: true },
  { name: "myPanels", value: false }
]

const DEFAULTS = {
    set: 3,
    repeat: 12,
    tolerance: 5,
    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    onAlternateDaysChecked: false,
    onAlternateDaysValue: 1,
    restInterval: 4
}

const PanelCreateDialog = ({panelCreateDialogVisibility, setMyPanel, setPanelCreateDialogVisibility, addedExercises, setAddedExercises, myPanels, t}: any) => {
    const { showToast } = useToastContext();
    
    const [selectedExercise, setSelectedExercise] = useState<any>({});
    const [panelName, setPanelName] = useState<string>("");
    const [isNewPanelSelected, setIsNewPanelSelected] = useState<boolean>(true);
    const [editModeEnabled, setEditModeEnabled] = useState<boolean>(false);
    const [editModeEnabledOnWhichPanel, setEditModeEnabledOnWhichPanel] = useState({ id: "" });

    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const [defaultValues, setDefaultValues] = useState(DEFAULTS);

    const {
        mutate: saveMyPanelMutation,
        data: saveMyPanelData,
        isSuccess: isSuccessSaveMyPanel,
        isPending: isPendingSaveMyPanel,
    } = useSaveMyPanel();

    const {
        mutate: updateMyPanelMutation,
        isSuccess: isSuccessUpdateMyPanel,
    } = useUpdateMyPanel();

    const validatePanel = () => {
        if(!panelName || panelName.trim() === "") {
            showToast({
                severity: "error",
                summary: "Error",
                detail: t("panelNameRequired"),
            });
            return false;
        }
        if(addedExercises.length === 0) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: t("atLeastOneExerciseRequired"),
            });
            return false;
        }
        return true;
    }

    const handleNewPanel = () => {
        if (!validatePanel()) return;

        const exerciseList = []

        for (const exercise of addedExercises) {
            if(exercise.isEveryOtherDay) {
                exerciseList.push(
                    {
                        procedureId: exercise.id,
                        isEveryOtherDay: exercise.isEveryOtherDay,
                        everyOtherDayPeriod: exercise.everyOtherDayPeriod,
                        days: [],
                        set: exercise.set,
                        repeat: exercise.repeat,
                        tolerance: exercise.tolerance,
                        restInterval: exercise.restInterval
                    }
                )
            } else {
                exerciseList.push(
                    {
                        procedureId: exercise.id,
                        isEveryOtherDay: false,
                        days: exercise.days,
                        set: exercise.set,
                        repeat: exercise.repeat,
                        tolerance: exercise.tolerance,
                        restInterval: exercise.restInterval
                    }
                )
            }
        }

        const newPanel = {
            name: panelName,
            exerciseProcedure: exerciseList
        }

        saveMyPanelMutation(newPanel)
    }

    const showError = (message: string) => {
        showToast({ severity: "error", summary: "Error", detail: t(message) });
    }

    const validateExercise = () => {
        if(!defaultValues.set || defaultValues.set <= 0 || defaultValues.set > 75) {
            showError("invalidSet");
            return false;
        }

        if(defaultValues.onAlternateDaysChecked && (defaultValues.onAlternateDaysValue <= 0 || defaultValues.onAlternateDaysValue > 7)) {
            showError("invalidAlternateDays");
            return false;
        }

        if(!defaultValues.onAlternateDaysChecked && !defaultValues.days.length) {
            showError("invalidDays");
            return false;
        }

        if(!defaultValues.repeat || defaultValues.repeat <= 0 || defaultValues.repeat > 30) {
            showError("invalidRepeat");
            return false;
        }

        if(!defaultValues.restInterval || defaultValues.restInterval <= 0 || defaultValues.restInterval > 60) {
            showError("invalidRestInterval");
            return false;
        }

        if(!defaultValues.tolerance || defaultValues.tolerance <= 0 || defaultValues.tolerance > 75) {
            showError("invalidTolerance");
            return false;
        }

        return true;
    }

    const updatePanel = (e: any) => {
        e.stopPropagation();
        
        updateMyPanelMutation(editModeEnabledOnWhichPanel)

    }

    const saveEditedExercise = () => {
        setAddedExercises((prev: any) => {
            return prev.map((item: any) => {
                if (item.id === selectedExercise.id) {
                    if (defaultValues.onAlternateDaysChecked) {
                        return {
                            ...item,
                            set: defaultValues.set,
                            days: [],
                            repeat: defaultValues.repeat,
                            restInterval: defaultValues.restInterval * 3600,
                            tolerance: defaultValues.tolerance,
                            isEveryOtherDay: defaultValues.onAlternateDaysChecked,
                            everyOtherDayPeriod: defaultValues.onAlternateDaysValue
                        }
                    }
                    return {
                        ...item,
                        set: defaultValues.set,
                        days: defaultValues.days,
                        repeat: defaultValues.repeat,
                        restInterval: defaultValues.restInterval * 3600,
                        tolerance: defaultValues.tolerance,
                        isEveryOtherDay: defaultValues.onAlternateDaysChecked,
                    };
                }
                return item;
            })
        })
        showToast({ severity: "success", summary: "Success", detail: `${selectedExercise?.name ?? selectedExercise?.exerciseName} updated successfully!` });
    }

    useEffect(() => {
        if (isSuccessUpdateMyPanel) {
            setMyPanel((prev:any) => {
                return prev.map((panel: any) => {
                    if (panel.id === editModeEnabledOnWhichPanel.id) {
                        return {
                            ...editModeEnabledOnWhichPanel,
                        };
                    }
                    return panel;
                })
            })
            showToast({
                severity: "success",
                summary: "Success",
                detail: "Panel updated successfully!",
            });
            setEditModeEnabledOnWhichPanel({ id: "" });
            setSelectedExercise({});
            setActiveIndex(null);
            setDefaultValues(DEFAULTS);
        }
    }, [isSuccessUpdateMyPanel])

    const saveEditedPostExercise = () => {
        setEditModeEnabledOnWhichPanel((prev:any) => {
            return {
                ...prev,
                exerciseProcedure: prev.exerciseProcedure.map((item: any) => {
                    if (item.procedureId === selectedExercise.procedureId) {
                        if (defaultValues.onAlternateDaysChecked) {
                            return {
                                ...item,
                                set: defaultValues.set,
                                days: [],
                                repeat: defaultValues.repeat,
                                restInterval: defaultValues.restInterval * 3600,
                                tolerance: defaultValues.tolerance,
                                isEveryOtherDay: defaultValues.onAlternateDaysChecked,
                                everyOtherDayPeriod: defaultValues.onAlternateDaysValue
                            }
                        }
                        return {
                            ...item,
                            set: defaultValues.set,
                            days: defaultValues.days,
                            repeat: defaultValues.repeat,
                            restInterval: defaultValues.restInterval * 3600,
                            tolerance: defaultValues.tolerance,
                            isEveryOtherDay: defaultValues.onAlternateDaysChecked,
                        };
                    }
                    return item;
                })
            }
        });
    }

    const handleSaveButton = () => {
        if (editModeEnabled) {
            if (validateExercise()) {
                if (isNewPanelSelected) {
                    saveEditedExercise();
                } else {
                    saveEditedPostExercise();
                }
            }
            setEditModeEnabled(false);
            setSelectedExercise({});
            setDefaultValues(DEFAULTS);
        } else {
            handleNewPanel();
        }
    }

    const selectButtonTemplate = (option:any) => {
        return (
            <div className="flex align-items-center gap-2">
                <img src={`${option.value ? (isNewPanelSelected ? "/panels-menu-img-white.svg" : "/panels-menu-img-gray.svg") : (isNewPanelSelected ? "/panels-menu-img-gray.svg" : "/panels-menu-img-white.svg")}`} alt={option.name} width={25} />
                <span>{t(option.name)}</span>
            </div>
        );
    }

    const setDefault = () => {
        setEditModeEnabled(false);
        setSelectedExercise({});
        setDefaultValues(DEFAULTS);
        setEditModeEnabledOnWhichPanel({ id: "" });
        setActiveIndex(null);
    }

    const handleSelectButtonChange = (e:any) => {
        setDefault();
        setIsNewPanelSelected(e.value);
    }

    const headerSelectButtonTemplate = (
        <SelectButton
            allowEmpty={false} 
            options={SELECTBUTTON_OPTIONS} 
            itemTemplate={selectButtonTemplate} 
            optionLabel="name" 
            optionValue="value" 
            className="newpanel-or-oldpanels-select-button flex gap-3" 
            value={isNewPanelSelected} 
            onChange={handleSelectButtonChange} 
        />
    )

    const saveButtonDisabled = () => {
        if (editModeEnabled) return false;
        return isNewPanelSelected ? (!panelName.trim() || addedExercises?.length === 0) : false;
    }

    const handleCloseDialog = () => {
        setPanelCreateDialogVisibility(false);
        setPanelName("");
        setIsNewPanelSelected(true);
        setDefault();
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            {(isNewPanelSelected || editModeEnabled) && <Button
                style={{
                    backgroundColor: "#E3506B",
                    width: "14rem",
                    height: "3rem",
                    borderRadius: "10px",
                    color: "white",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem"
                }}
                onClick={handleSaveButton}
                disabled={saveButtonDisabled()}
                loading={isPendingSaveMyPanel}
            >
                {!isPendingSaveMyPanel && <img src="/accept.svg" />}
                <p className="m-0 font-bold" style={{ margin: 0 }}>{t("save")}</p>
            </Button>}
        </div>
    )

    const handleCloseEditMode = () => {
        setEditModeEnabled(false);
        setDefaultValues(DEFAULTS);
        setSelectedExercise({});
    }

    const headerReturn = (
        <div className="flex gap-3">
            <Button rounded text onClick={handleCloseEditMode} className="p-1" style={{backgroundColor: "transparent"}}>
                <img src="/goBack.svg" />
            </Button>
            <span className="text-xl">{selectedExercise?.name ?? selectedExercise?.exerciseName}</span>
        </div>
    )

    const renderHeaderContent = () => {
        switch (editModeEnabled) {
            case true:
                return headerReturn;
            case false:
                return headerSelectButtonTemplate;
        }
    }

    const handleSelectExerciseOnEdit = (exercise: any) => {
        setEditModeEnabled(true)
        setDefaultValues({
            set: exercise.set,
            repeat: exercise.repeat,
            tolerance: exercise.tolerance,
            days: exercise.days,
            onAlternateDaysChecked: exercise.isEveryOtherDay,
            onAlternateDaysValue: exercise.everyOtherDayPeriod || 1,
            restInterval: Math.round(exercise.restInterval / 3600) || Math.round(12 / exercise.set),
        });
        setSelectedExercise(exercise)
    }

    const handleDeleteExercise = (exercise: any) => {
        setAddedExercises((prev: any) => prev.filter((item: any) => item.id !== exercise.id));
    }

    const handleDeleteNewPanel = (e: any) => {
        setAddedExercises([]);
        setPanelName("");
    }

    const render = () => {
        if (editModeEnabled) return <EditMode defaultValues={defaultValues} setDefaultValues={setDefaultValues} t={t} />;
        switch(isNewPanelSelected) {
            case true:
                return <NewPanelSection setAddedExercises={setAddedExercises} addedExercises={addedExercises} handleDeleteExercise={handleDeleteExercise} handleSelectExerciseOnEdit={handleSelectExerciseOnEdit} panelName={panelName} setPanelName={setPanelName} handleDeleteNewPanel={handleDeleteNewPanel} t={t} />
            case false:
                return <OldPanelsSection activeIndex={activeIndex} setActiveIndex={setActiveIndex} myPanels={myPanels} setMyPanel={setMyPanel} handleSelectExerciseOnEdit={handleSelectExerciseOnEdit} editModeEnabledOnWhichPanel={editModeEnabledOnWhichPanel} setEditModeEnabledOnWhichPanel={setEditModeEnabledOnWhichPanel} updatePanel={updatePanel} t={t} />
        }
    }

    useEffect(() => {
        if (isSuccessSaveMyPanel) {
            showToast({
                severity: "success",
                summary: "Success",
                detail: "Panel saved successfully!",
            });
            setMyPanel((prevPanel: any) => prevPanel ? [...prevPanel, saveMyPanelData] : [saveMyPanelData]);
            handleCloseDialog();
            setAddedExercises([]);
        }
    }, [isSuccessSaveMyPanel]);

    return (
        <Dialog
            header={renderHeaderContent()}
            footer={footer}
            visible={panelCreateDialogVisibility}
            blockScroll
            draggable={false}
            style={{ width: '50vw', borderRadius: '24px', overflow: "hidden" }}
            onHide={handleCloseDialog}
        >
            {render()}
        </Dialog>
    )
}

export default PanelCreateDialog;